import { ok, numbersIn, type Action, type I18n, type Principal, type Result, type ScreenManifest } from '@erp-sslab/sdk-frontend';

/**
 * Stale-while-offline reads (docs/design/offline-outbox.md §4–§5, phase 5): a screen whose manifest
 * declares `source.offline_read: true` keeps the last answer its route gave the person signed in, so
 * that when the route cannot be reached the screen draws that answer under «Offline — shown as of …»
 * instead of a refusal, with every action that would ask the server held.
 *
 * What is kept, and what never is:
 * - Only the answers of screens that declare it (and their manifest, without which the answer cannot
 *   be drawn), only as this person was given them with their own token, keyed by workspace and
 *   person (`reads:<tenant>:<user>`), so a shared phone never shows one person's copy to another.
 * - At most `READS_KEEP` answers per person, none older than `READS_DAYS` days.
 * - Never a token, a password or any other secret: no header is kept, and a property named like one
 *   (`token`, `*_secret`, `password`, …) is dropped from the answer before it is stored (`scrubbed`).
 * - Nothing in the service worker, nothing in `localStorage`. Signing out deletes the whole store.
 *
 * Online, the network's answer always wins and replaces the copy; the copy is read only when the
 * route gave no answer at all (`noAnswer`) — never in place of a refusal the server actually made.
 */

/** The IndexedDB database the copies live in; deleted whole on sign-out. */
export const READS_DB = 'core-reads';
const STORE = 'reads';
/** How many answers one person keeps; the oldest goes first. */
export const READS_KEEP = 30;
/** How long a copy may be shown; an older one is deleted when it is next touched. */
export const READS_DAYS = 7;
const DAY_MS = 86_400_000;

/** One kept answer: what the route said, and when. */
export interface KeptRead { key: string; data: unknown; at: number }

/** Where the copies live — IndexedDB in a browser, a map in a test. Every call is about one owner's copies. */
export interface ReadBackend {
  get(owner: string, key: string): Promise<KeptRead | null>;
  put(owner: string, read: KeptRead): Promise<void>;
  list(owner: string): Promise<{ key: string; at: number }[]>;
  remove(owner: string, keys: readonly string[]): Promise<void>;
  /** Everything, every owner's: signing out. */
  clear(): Promise<void>;
}

/** Whose copies these are: the workspace and the person, or null when nobody is in a workspace. */
export function ownerOf(principal: Principal | null): string | null {
  if (!principal?.sub || !principal.tenant_id) return null;
  return `reads:${principal.tenant_id}:${principal.sub}`;
}

/** The key one answer is kept under: the screen it was drawn on and the path it was read from. */
export function readKey(kind: 'manifest' | 'answer' | 'options', screen: string, path = ''): string {
  return `${kind}:${screen}:${path}`;
}

/**
 * Whether a read got no answer at all — no connection, a timeout, an edge that could not reach the
 * unit — as against a service that answered and refused (a 404, a 403), which is the truth and is
 * shown as it is.
 */
export function noAnswer(r: Result<unknown>): boolean {
  return r.state === 'timeout' || (r.state === 'unavailable' && r.status === undefined);
}

/** A property that holds a credential or a secret, wherever it sits in an answer. */
const SECRET_NAME = /^(?:token|password|passcode|secret|otp|api_?key|private_key|vault_ref|sealed)$|(?:^|_)(?:token|password|secret|otp|api_?key|private_key)$/i;

/** The answer with every property named like a secret left out, at any depth. */
export function scrubbed(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(scrubbed);
  if (!value || typeof value !== 'object') return value;
  const out: Record<string, unknown> = {};
  for (const [name, v] of Object.entries(value as Record<string, unknown>)) {
    if (SECRET_NAME.test(name)) continue;
    out[name] = scrubbed(v);
  }
  return out;
}

/** The copies kept in memory: for tests, and the shape the IndexedDB backend answers. */
export function memoryBackend(): ReadBackend {
  const owners = new Map<string, Map<string, KeptRead>>();
  const of = (owner: string) => { let m = owners.get(owner); if (!m) { m = new Map(); owners.set(owner, m); } return m; };
  return {
    get: async (owner, key) => of(owner).get(key) ?? null,
    put: async (owner, read) => { of(owner).set(read.key, structuredClone(read)); },
    list: async (owner) => [...of(owner).values()].map((r) => ({ key: r.key, at: r.at })),
    remove: async (owner, keys) => { for (const k of keys) of(owner).delete(k); },
    clear: async () => { owners.clear(); },
  };
}

/** IndexedDB request as a promise. */
function done<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => { req.onsuccess = () => resolve(req.result); req.onerror = () => reject(req.error); });
}

/**
 * The copies in IndexedDB: one database, one store, each row `{id: owner + key, owner, key, data, at}`
 * with an index on its owner. Null where the browser has none (a test, some private windows); every
 * failure of a browser that has one is swallowed by `OfflineReads` — a copy is a courtesy.
 */
export function indexedDbBackend(factory: IDBFactory | undefined = globalThis.indexedDB): ReadBackend | null {
  if (!factory) return null;
  let opening: Promise<IDBDatabase> | null = null;
  const open = () => {
    opening ??= new Promise<IDBDatabase>((resolve, reject) => {
      const req = factory.open(READS_DB, 1);
      req.onupgradeneeded = () => {
        const store = req.result.createObjectStore(STORE, { keyPath: 'id' });
        store.createIndex('owner', 'owner', { unique: false });
      };
      req.onsuccess = () => {
        const db = req.result;
        // Another tab deleting the database (its sign-out) closes this one rather than blocking it.
        db.onversionchange = () => { db.close(); opening = null; };
        resolve(db);
      };
      req.onerror = () => { opening = null; reject(req.error); };
      req.onblocked = () => { opening = null; reject(new Error('blocked')); };
    });
    return opening;
  };
  const store = async (mode: IDBTransactionMode) => (await open()).transaction(STORE, mode).objectStore(STORE);
  const id = (owner: string, key: string) => `${owner}\n${key}`;
  return {
    get: async (owner, key) => {
      const row = await done((await store('readonly')).get(id(owner, key))) as (KeptRead & { owner: string }) | undefined;
      return row && row.owner === owner ? { key: row.key, data: row.data, at: row.at } : null;
    },
    put: async (owner, read) => { await done((await store('readwrite')).put({ id: id(owner, read.key), owner, ...read })); },
    list: async (owner) => {
      const rows = await done((await store('readonly')).index('owner').getAll(owner)) as KeptRead[];
      return rows.map((r) => ({ key: r.key, at: r.at }));
    },
    remove: async (owner, keys) => {
      if (!keys.length) return;
      const s = await store('readwrite');
      await Promise.all(keys.map((k) => done(s.delete(id(owner, k)))));
    },
    clear: async () => {
      const was = opening;
      opening = null;
      if (was) { try { (await was).close(); } catch { /* it never opened */ } }
      await new Promise<void>((resolve) => {
        const req = factory.deleteDatabase(READS_DB);
        // Blocked by another tab still holding it: that tab closes on `versionchange`, and the
        // deletion completes once it has; not waiting for it here keeps sign-out quick.
        req.onsuccess = () => resolve(); req.onerror = () => resolve(); req.onblocked = () => resolve();
      });
    },
  };
}

/** The copies of one browser, kept and read back under the rules above. */
export class OfflineReads {
  private readonly backend: ReadBackend | null;
  private readonly now: () => number;

  constructor(backend: ReadBackend | null, now: () => number = Date.now) {
    this.backend = backend;
    this.now = now;
  }

  /** Keeps an answer, then lets go of what is too old or beyond the newest `READS_KEEP`. */
  async keep(owner: string, key: string, data: unknown): Promise<void> {
    if (!this.backend) return;
    try {
      await this.backend.put(owner, { key, data: scrubbed(data), at: this.now() });
      const all = await this.backend.list(owner);
      const cutoff = this.now() - READS_DAYS * DAY_MS;
      const fresh = all.filter((r) => r.at >= cutoff).sort((a, b) => b.at - a.at);
      const gone = [...all.filter((r) => r.at < cutoff), ...fresh.slice(READS_KEEP)].map((r) => r.key);
      await this.backend.remove(owner, gone);
    } catch { /* a copy is a courtesy: a full or blocked store keeps nothing */ }
  }

  /** The kept answer, while it is young enough to show; an expired one is deleted and not shown. */
  async recall(owner: string, key: string): Promise<KeptRead | null> {
    if (!this.backend) return null;
    try {
      const read = await this.backend.get(owner, key);
      if (!read) return null;
      if (this.now() - read.at > READS_DAYS * DAY_MS) { await this.backend.remove(owner, [key]); return null; }
      return read;
    } catch { return null; }
  }

  /** Every copy of every person on this browser: signing out. */
  async forget(): Promise<void> {
    try { await this.backend?.clear(); } catch { /* nothing was kept, or the browser has already cleared it */ }
  }
}

/** The browser's copies. A page with no IndexedDB keeps nothing and shows nothing offline. */
export const offlineReads = new OfflineReads(indexedDbBackend());

/** What a read came to, and — when it is a kept copy rather than the network's answer — when it was kept. */
export interface OfflineRead<T> { result: Result<T>; asOf: number | null }

/**
 * A screen's manifest, read through the copies: kept when it declares `offline_read`, and recalled
 * when the unit gives no answer — only a manifest that declared it was ever kept, so a recalled one
 * always does.
 */
export async function readManifest(reads: OfflineReads, owner: string | null, screen: string, ask: () => Promise<Result<ScreenManifest>>): Promise<OfflineRead<ScreenManifest>> {
  const r = await ask();
  if (!owner) return { result: r, asOf: null };
  const key = readKey('manifest', screen);
  if (r.state === 'ok') {
    if (r.data.source?.offline_read === true) await reads.keep(owner, key, r.data);
    return { result: r, asOf: null };
  }
  if (!noAnswer(r)) return { result: r, asOf: null };
  const kept = await reads.recall(owner, key);
  return kept ? { result: ok(kept.data as ScreenManifest), asOf: kept.at } : { result: r, asOf: null };
}

/**
 * A screen's answer — a list's rows, a record — read through the copies on a manifest that declares
 * `offline_read`: the network's answer kept and returned; with no answer, the copy and its time.
 * A manifest that does not declare it is read as it always was.
 */
export async function readAnswer<T>(reads: OfflineReads, owner: string | null, manifest: ScreenManifest, path: string, ask: () => Promise<Result<T>>): Promise<OfflineRead<T>> {
  const r = await ask();
  if (!owner || manifest.source?.offline_read !== true) return { result: r, asOf: null };
  const key = readKey('answer', manifest.screen, path);
  if (r.state === 'ok') { await reads.keep(owner, key, r.data); return { result: r, asOf: null }; }
  if (!noAnswer(r)) return { result: r, asOf: null };
  const kept = await reads.recall(owner, key);
  return kept ? { result: ok(kept.data as T), asOf: kept.at } : { result: r, asOf: null };
}

/**
 * Whether a record part's manifest is kept on this device: it keeps its own answer (`offline_read`),
 * or it adds an action that may wait to be sent (`offline: true`) — without its manifest the part,
 * and the action on it, could not be drawn over a kept copy of the record.
 */
export function keepsPart(manifest: ScreenManifest): boolean {
  return manifest.source?.offline_read === true || (manifest.actions ?? []).some((a) => a.offline === true);
}

/** The key a record part's manifest is kept under: apart from the screens', whose keys are `module.screen` too. */
export function partKey(part: string): string {
  return `part:${part}`;
}

/**
 * A record part's manifest, read through the copies (the outbox on record parts, offline-outbox.md
 * §2): kept when `keepsPart`, and recalled when its unit gives no answer — drawn then with every
 * action that would ask the server held, and its `offline: true` ones left to the outbox.
 */
export async function readPartManifest(reads: OfflineReads, owner: string | null, part: string, ask: () => Promise<Result<ScreenManifest>>): Promise<OfflineRead<ScreenManifest>> {
  const r = await ask();
  if (!owner) return { result: r, asOf: null };
  const key = readKey('manifest', partKey(part));
  if (r.state === 'ok') {
    if (keepsPart(r.data)) await reads.keep(owner, key, r.data);
    return { result: r, asOf: null };
  }
  if (!noAnswer(r)) return { result: r, asOf: null };
  const kept = await reads.recall(owner, key);
  return kept ? { result: ok(offlineManifest(kept.data as ScreenManifest)), asOf: kept.at } : { result: r, asOf: null };
}

/**
 * The choices a form reads for a field (`options_from`, a lookup's list), read through the copies —
 * only for a form the outbox may keep (`offline: true`): there, a form filled in with no connection
 * still needs its lists. Kept per workspace and person with the other copies (`options:<unit>:<path>`,
 * the same 30-answer, 7-day bound, scrubbed, deleted by `forgetDevice`); recalled only when the route
 * gave no answer, with the moment it was kept, so the form can say the choices may be out of date.
 * Any other form reads as it always did.
 */
export async function readOptions<T>(reads: OfflineReads, owner: string | null, action: Pick<Action, 'offline'>, base: string, path: string, ask: () => Promise<Result<T>>): Promise<OfflineRead<T>> {
  const r = await ask();
  if (!owner || action.offline !== true) return { result: r, asOf: null };
  const key = readKey('options', base, path);
  if (r.state === 'ok') { await reads.keep(owner, key, r.data); return { result: r, asOf: null }; }
  if (!noAnswer(r)) return { result: r, asOf: null };
  const kept = await reads.recall(owner, key);
  return kept ? { result: ok(kept.data as T), asOf: kept.at } : { result: r, asOf: null };
}

/** The catalogue key of the reason a held action gives while the screen is a kept copy. */
export const OFFLINE_HELD = 'shell.offline.held';

/** Whether pressing the action asks the server — everything but opening another screen. */
export function asksServer(action: Action): boolean {
  return !(action.screen && !action.request && !action.component);
}

/**
 * Whether the action is held on a kept copy: it asks the server, and it may not wait on this device
 * for the connection to come back — an `offline: true` form goes through, to the outbox (outbox.ts).
 */
export function heldOffline(action: Action): boolean {
  return asksServer(action) && action.offline !== true;
}

/**
 * The action as it is offered on a kept copy: held, with the reason said beside it, when it would ask
 * the server (a write, a file, an editor); unchanged when it only opens another screen, or when it is
 * a form the outbox keeps to send later (`offline: true`).
 */
export function holdOffline(action: Action): Action {
  if (!heldOffline(action)) return action;
  return { ...action, available: { path: action.available?.path ?? '/', property: 'offline', otherwise: 'disable', reason_key: OFFLINE_HELD, held: 'no' } };
}

/** A manifest drawn from a kept copy: each of its actions as `holdOffline` offers it. */
export function offlineManifest(manifest: ScreenManifest): ScreenManifest {
  return manifest.actions?.length ? { ...manifest, actions: manifest.actions.map(holdOffline) } : manifest;
}

/**
 * What the notice over a kept copy says: «Offline — shown as of 7:42 ص …», on the 12-hour clock with
 * Western digits, and the day too when it was not today.
 */
export function offlineWords(asOf: number, i18n: I18n, now: Date = new Date()): string {
  return i18n.t('shell.offline.asOf', { when: offlineWhen(asOf, i18n, now) });
}

/** What a form says over choices read from this device's copy: «Offline — the choices are as they were at 7:42 ص …». */
export function offlineChoicesWords(asOf: number, i18n: I18n, now: Date = new Date()): string {
  return i18n.t('shell.offline.choices', { when: offlineWhen(asOf, i18n, now) });
}

/** The moment a copy was kept, on the 12-hour clock with Western digits, with its day when not today. */
function offlineWhen(asOf: number, i18n: I18n, now: Date): string {
  const at = new Date(asOf);
  const locale = numbersIn(i18n.locale);
  const time = at.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit', hour12: true });
  const today = at.toDateString() === now.toDateString();
  return today ? time : `${at.toLocaleDateString(locale, { day: 'numeric', month: 'long' })} ${time}`;
}
